<template>
  <tutorial-layout title="Slots" :referenceLinks="referenceLinks" :youtubeLinks="youtubeLinks">
    <p slot="overview">
      Slots are another way in Vue for a component to inject content into a child component or from a child to parent without using emit. 
      In terms of final output, slots perform a similar function as props in Vue, but can work with more complex data. 
      Slots are often used to modify default component template or its parts, as well as the actions.
    </p>
    <p slot="benefits">
      <ul>
        <li>
          Child component is more reusable — you can pass it different types of data (including html and other components) without worrying about a consistent format or data values
        </li>
        <li>
          It’s a lot more flexible — you don’t always have to fill every value whereas with props, you’d have to worry about checking if values exist using v-if
        </li>
        <li>
          Many vuetify components have default named slots we can use out of the box. 
        </li>
      </ul>
    </p>
    <p> There are 3 main usage scenarios for slots: </p>
    <br/>
    <v-expansion-panels multiple accordion xl-12>
      
      <!-- Default Slots -->
      <expansionpnl>
        
        <template v-slot:content>
          <p>In this example we have a component called <codesimple>basicbtn</codesimple> with a default slot for the button name, which we are able to modify when we call this component on the parent. 
             We will be setting <i>"New Button"</i> as a button text on the left example and just call an empty component on the right exmple to see the difference.</p>
          <v-row>
            <v-col>
              <p>Parent Component</p>
          <pre><code>&lt;basicbtn&gt;New Button&lt;/basicbtn&gt;</code></pre>
          <br/>
          
          <p>Child Component <codesimple>basicbtn</codesimple></p>
          <pre><code>&lt;template&gt;
  &lt;v-btn elevation="10" depressed color="primary"&gt;
    &lt;slot&gt;Default Button&lt;/slot&gt;
  &lt;/v-btn&gt;
&lt;/template&gt;</code></pre>
          <br/>
          
          <p>Result</p>
          <basicbtn>New Button</basicbtn>
            </v-col>

            <v-col>
              <p>Parent Component - not defining the content for the default slot</p>
          <pre><code>&lt;basicbtn&gt;&lt;/basicbtn&gt;</code></pre>
          <br/>
          
          <p>Child Component <i>basicbtn</i></p>
          <pre><code>&lt;template&gt;
  &lt;v-btn elevation="10" depressed color="primary"&gt;
    &lt;slot&gt;Default Button&lt;/slot&gt;
  &lt;/v-btn&gt;
&lt;/template&gt;</code></pre>
          <br/>
          
          <p>Result</p>
          <basicbtn></basicbtn>
            </v-col>
          </v-row>
          
        
        </template>

      </expansionpnl>
      
      <!-- Named Slots -->
      <expansionpnl>
        
        <template v-slot:header >
          Named Slots
        </template>

        <template v-slot:content>
          <p>
            Named slots allowing us to pass certain data/template to the specific place in the child component. On this example we are passing Form Header and Form Content, but keeping default footer.
          </p>
          <v-row>
            <v-col>          
          <p>Parent Component</p>
          <pre><code>&lt;basicform&gt;
  &lt;h1 slot="header"&gt;Header from the Parent&lt;/h1&gt;
  &lt;div slot="content"&gt;Add content with a button and onclick event.&lt;/div&gt;
&lt;/basicform&gt;</code></pre>
          

          <p>Child Component</p>
          <pre><code>&lt;template&gt;
  &lt;v-form&gt;
    &lt;h3&gt;&lt;slot name="header"&gt;&lt;/slot&gt;&lt;/h3&gt;
    &lt;v-content&gt;&lt;slot name="content"&gt;&lt;/slot&gt;&lt;/v-content&gt;
    &lt;v-footer&gt;=======just footer======&lt;/v-footer&gt;
  &lt;/v-form&gt;
&lt;/template&gt;</code></pre>
          </v-col>
          <v-col>

          <p>Result</p>
          <basicform>
            <h1 slot="header">Header from the Parent</h1>
            <div slot="content">Add content with a button and onclick event.</div>
          </basicform>
        </v-col>
        </v-row>         
        </template>

      </expansionpnl>

      <!-- Scoped Slots and Slot Props -->
      <expansionpnl level="advanced">
        
        <template v-slot:header>
          Scoped Slots (Slot Props)
        </template>

        <template v-slot:content>
          <h3>Slot Props</h3>
          <p>
            Scoped slots are used to pass the reference to a data from child to parent component. Attributes bound to a slot element are called slot props. <br/>
            In the example below we have a component called <codesimple>slotpropuser</codesimple>, and we want to redefine on the parent component how user name and position are displayed. 
          </p>
          <v-row>
            <v-col>
               <p>Parent Component</p>
          <pre><code>&lt;slotpropuser&gt;
  &lt;template v-slot:default="slotProps"&gt;
    &lt;b style="color:green"&gt;{{slotProps.user.Name}}&lt;/b&gt;
  &lt;/template&gt;
  &lt;template v-slot:position="slotProps"&gt;
    &lt;b style="color:yellow"&gt;{{slotProps.position}}&lt;/b&gt;
  &lt;/template&gt;
&lt;/slotpropuser&gt;</code></pre>
          

          <p>Child Component</p>
          <pre><code>&lt;template&gt;
  &lt;span&gt;
    &lt;span&gt;
      User Name: &lt;br /&gt;
      &lt;slot :user="user"&gt;{{ user.Name }}&lt;/slot&gt;&lt;/span&gt;
    &lt;br /&gt;
    &lt;span&gt;&lt;slot name="position" :position="user.JobTitle"&gt;&lt;/slot&gt;&lt;/span&gt;
  &lt;/span&gt;
&lt;/template&gt;</code></pre>
            </v-col>
            <v-col>
              <slotpropuser>
                <template v-slot:default="slotProps">
                  <b style="color:green">{{slotProps.user.Name}}</b>
                </template>
                <template v-slot:position="slotProps">
                  <b style="color:yellow">{{slotProps.position}}</b>
                </template>
              </slotpropuser> 
            </v-col>            
          </v-row>

        </template>
      </expansionpnl>
      
    </v-expansion-panels>
    <br/>
    <v-divider></v-divider>
    <br/>
    <p><i>* This tutorial page was buiild using slots, so you can open its source in the @\apps\tutorials\views\Slots and explore the components using slots: basic-btn, basic-form, cose-simple, expansion-pnl, slot-prop-user. </i></p>
  </tutorial-layout>
</template>

<script>
import uiView from "@/_mixins/ui-view-mixin";
import expansionpnl from "./expansion-pnl";
import basicbtn from "./basic-btn";
import basicform from "./basic-form";
import codesimple from "./code-simple";
import tutorialLayout from "../../components/tutorial-layout";
import slotpropuser from "./slot-prop-user";

export default {
  name: "VModel",
  mixins: [uiView],
  created() {},
  data: () => ({
      text1: "Example Text",
      user: {
        lastName: "{{ user.lastName }}",
      },
      slotProps: {
        user: {
          lastName: "{{ slotProps.user.lastName }}",
        },
      },
  }),
  components: { tutorialLayout, expansionpnl, basicbtn, codesimple, basicform, slotpropuser },
  props: {},
  watch: {},
  computed: {
    referenceLinks() {
      return [
        {
          title: "v-slot overview (vuejys.org)",
          href: "https://vuejs.org/v2/guide/components-slots.html",
        },
        {
          title: "Use Component Slots in Vue.js",
          href: "https://www.digitalocean.com/community/tutorials/vuejs-component-slots",
        },
        {
          title: "Vue Slots",
          href: "https://flaviocopes.com/vue-slots/",
        }
      ];
    },
    youtubeLinks(){
      return [
        {
          title: "V-Slot Introduction",
          href: "https://www.youtube.com/embed/F44OoFk8spg",
        },
        {
          title: "Usuage of V-Slot",
          href: "https://www.youtube.com/embed/huGdfuwj2hQ",
        },
        {
          title: "V-Slot tutorial",
          href: "https://www.youtube.com/embed/qCrogbJSHnk",
        },
      ]
    }
  },
  methods: {},
};
</script>
<style lang="scss">
pre {
  display: flex;
  max-width: 100%;
  flex: 0 0 100%;
  code {
    margin: 0px !important;
    width: 100%;
  }
}
</style>
